/**
 * USER-IDENTIFIER leaf: the id-prefix strip, the cleaners and normalizers built on it, and the `IdentitySource`
 * caller-id resolution that consumes it. ONE family, not two -- `resolveAgentRunnerUserId` CALLS
 * {@link stripIdPrefixOrKeep}, and `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` bars a utils-to-utils value import, so the
 * resolver and the strip it depends on cannot be separated into sibling leaves at this layer. Membership rule: a
 * symbol belongs here when it reads or rewrites a USER id.
 *
 * The case-folding family that used to share this file (`normalizeString`, `createStatusNormalizer`) reads no id and
 * was called by nothing here, so it left when this leaf was renamed off the contentless `helpers` spelling; it now
 * lives on the const floor (`const/string-normalize.ts`), where a utils reader barred from a utils sibling can reach it.
 *
 * The trim/'null' decision `cleanAndStripPrefix` starts from is `trimToNullDroppingNullLiteral` (`const/string-guards.ts`),
 * the one spelling `cleanInput` (`utils/data/clean.ts`) reads too: the utils floor bars importing that sibling, so the
 * shared decision lives on the const floor both may reach.
 */
import { ID_PREFIX_STRIP_REGEX } from '@federa/const/id-prefixes';
import { isNonEmptyString, stringOrEmpty as strOr, trimToNullDroppingNullLiteral } from '@federa/const/string-guards';
import { KIND_AGENT_RUNNER, KIND_WEBHOOK } from '@federa/const/kind-values';
import type { IdentitySource } from '@federa/types/caller-identity-source';

/** Guest users have limited access and cannot perform certain operations. */
export function isGuestUser(referenceId: string): boolean {
	if (!isNonEmptyString(referenceId)) return false;
	return referenceId.toLowerCase().includes('guest');
}

/** Null when the input is not a non-empty string. */
export function stripIdPrefix(id: string): string | null {
	if (!isNonEmptyString(id)) return null;
	return id.replace(ID_PREFIX_STRIP_REGEX, '');
}

/** Eliminates the repeated `stripIdPrefix(x) || x` defensive pattern. */
export function stripIdPrefixOrKeep(id: string): string {
	return stripIdPrefix(id) ?? id;
}

/** Trim/null handling plus stripIdPrefix; null when the input was empty. */
export function cleanAndStripPrefix(input: string | null | undefined): string | null {
	const cleaned = trimToNullDroppingNullLiteral(input);
	return typeof cleaned === 'string' ? stripIdPrefix(cleaned) : null;
}

/** Webhook envelope precedence: globalVar.user_id, data.profileId, data.userId, input.userId. */
function resolveWebhookUserId(source: Extract<IdentitySource, { kind: typeof KIND_WEBHOOK }>): string {
	const { data, input, globalVar } = source;
	return strOr(globalVar?.user_id) || strOr(data.profileId) || strOr(data.userId) || strOr(input.userId) || '';
}

/** Agent-runner precedence: gvar.profileId, gvar.reference_id (id-prefix-stripped). */
function resolveAgentRunnerUserId(source: Extract<IdentitySource, { kind: typeof KIND_AGENT_RUNNER }>): string {
	const { gvar } = source;
	const raw = strOr(gvar.profileId) || strOr(gvar.reference_id);
	return stripIdPrefixOrKeep(raw);
}

/** Returns '' when no field is populated; callers map '' to unauthorized. */
export function resolveCallerUserId(source: IdentitySource): string {
	switch (source.kind) {
		case KIND_WEBHOOK:
			return resolveWebhookUserId(source);
		case KIND_AGENT_RUNNER:
			return resolveAgentRunnerUserId(source);
	}
}

/** Clean a user ID by stripping prefixes (`pr_`, `ph_`). Canonical user-id cleaner: the profiles table holds plain UUIDs, so a prefixed id is always a caller-side artefact. Lives beside {@link stripIdPrefix}, which defines the strip, because the utils floor bars a sibling value import. */
export function cleanUserId(userId: string | null | undefined): string | null | undefined {
	if (!userId) return userId;
	return stripIdPrefix(userId);
}

/** Expand a user ID into the array of spellings a lookup must try. Plain UUIDs, so this is a single-element array -- it exists so callers do not re-derive the (now empty) prefix vocabulary. */
export function expandUserIdWithPrefixes(userId: string): string[] {
	return [stripIdPrefixOrKeep(userId)];
}

/**
 * Normalize an OPTIONAL viewer id into the routing pair a `prepare` step answers: whether a viewer was supplied at
 * all, and the stripped id to look them up by (empty string when there is none).
 *
 * `has_user` is a ROUTING key -- the graphs that consume this branch their access subflow on it -- so the two members
 * must agree by construction: a non-empty `lookup_user_id` beside `has_user: false` would run the lookup against an id
 * the graph decided not to have. Deriving both from one length test is what guarantees that, and it is why this is one
 * function rather than two expressions each call site re-spells.
 *
 * Lives beside {@link stripIdPrefixOrKeep}, which defines the strip, for the reason {@link cleanUserId} records: the
 * utils floor bars a sibling value import, so a normalizer over that strip has to share its leaf.
 */
export function prepareViewerLookup(userId: unknown): { has_user: boolean; lookup_user_id: string } {
	const id = userId != null ? String(userId) : '';
	return { has_user: id.length > 0, lookup_user_id: id.length > 0 ? stripIdPrefixOrKeep(id) : '' };
}
